/**
 * Timeline Date Utilities
 *
 * Date and week calculation utilities for timeline rendering.
 */

import { WEEK_MS } from "../timeline-constants"

/**
 * Calculate timeline date range from frost dates with offsets
 *
 * @param {Object} frostDateRange - { lfd, ffd } Date objects
 * @param {number} startOffset - Months before LFD
 * @param {number} endOffset - Months after FFD
 * @returns {Object} { start, end } Date objects
 */
export const calculateTimelineDateRange = (frostDateRange, startOffset, endOffset) => {
  if (!frostDateRange) return {}

  const start = new Date(frostDateRange.lfd)
  start.setMonth(start.getMonth() - startOffset, 1)

  const end = new Date(frostDateRange.ffd)
  end.setMonth(end.getMonth() + endOffset, 31)

  return { start, end }
}

/**
 * Calculate total weeks between dates
 *
 * @param {Object} dateRange - { start, end } Date objects
 * @returns {number} Total weeks (rounded up)
 */
export const calculateTotalWeeks = (dateRange) => {
  if (!dateRange || !dateRange.start || !dateRange.end) return 0
  return Math.ceil((dateRange.end - dateRange.start) / (WEEK_MS))
}

/**
 * Generate weeks array with sun intensity and month data
 *
 * Sun intensity uses a cosine wave to simulate seasonal daylight variation:
 * - Peak intensity at middle of timeline (summer)
 * - Low intensity at edges (winter)
 *
 * @param {Object} timelineDateRange - { start, end } Date objects
 * @param {number} totalWeeks - Number of weeks to generate
 * @returns {Object} { weeks, months }
 *   - weeks: Array of { dayNum, month, monthKey, sunColor }
 *   - months: Object mapping monthKey → week count
 */
export const generateWeeksAndMonths = (timelineDateRange, totalWeeks) => {
  if (!timelineDateRange || !timelineDateRange.start || totalWeeks === 0) {
    return { weeks: [], months: {} }
  }

  const weeks = Array.from({ length: totalWeeks }).map((_, i) => {
    // Calculate sun intensity using cosine wave (0 = winter, 1 = summer)
    const intensity = (Math.cos(((i - totalWeeks / 2) / totalWeeks) * 2 * Math.PI) + 1) / 2

    // Generate sun color gradient (pale blue → bright yellow)
    const sunColor = `rgb(
      ${Math.floor(100 + intensity * 155)},
      ${Math.floor(150 + intensity * 50)},
      ${Math.floor(255 - intensity * 255)}
    )`

    // Calculate date for this week
    const d = new Date(timelineDateRange.start)
    d.setDate(d.getDate() + i * 7)

    // Generate unique month key (includes year to handle multi-year timelines)
    const monthKey = `${d.toLocaleString('en-US', { month: 'long' })} ${d.getFullYear()}`

    return {
      dayNum: d.getDate(),
      month: d.toLocaleString('en-US', { month: 'long' }),
      monthKey,
      sunColor
    }
  })

  // Group weeks by month (count weeks per month)
  const months = weeks.reduce((acc, week) => {
    acc[week.monthKey] = (acc[week.monthKey] || 0) + 1
    return acc
  }, {})

  return { weeks, months }
}

/**
 * Get current date string formatted for display
 *
 * @returns {string} Formatted date (e.g., "Feb 10")
 */
export const getNowDateString = () => {
  return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
